import * as i0 from '@angular/core';
import { input, booleanAttribute, output, HostListener, Directive } from '@angular/core';
import { setupFormControl } from 'ng-primitives/form-field';
import { setupInteractions } from 'ng-primitives/internal';
import { uniqueId } from 'ng-primitives/utils';
import { createStateToken, createStateProvider, createStateInjector, createState } from 'ng-primitives/state';

/**
 * The state token  for the Checkbox primitive.
 */
const NgpCheckboxStateToken = createStateToken('Checkbox');
/**
 * Provides the Checkbox state.
 */
const provideCheckboxState = createStateProvider(NgpCheckboxStateToken);
/**
 * Injects the Checkbox state.
 */
const injectCheckboxState = createStateInjector(NgpCheckboxStateToken);
/**
 * The Checkbox state registration function.
 */
const checkboxState = createState(NgpCheckboxStateToken);

/**
 * Apply the `ngpCheckbox` directive to an element to that represents the checkbox, such as a `button`.
 */
let NgpCheckbox = /*#__PURE__*/(() => {
  class NgpCheckbox {
    constructor() {
      /**
       * The id of the checkbox.
       * @internal
       */
      this.id = input(uniqueId('ngp-checkbox'));
      /**
       * Defines whether the checkbox is checked.
       */
      this.checked = input(false, {
        alias: 'ngpCheckboxChecked',
        transform: booleanAttribute
      });
      /**
       * The event that is emitted when the checkbox value changes.
       */
      this.checkedChange = output({
        alias: 'ngpCheckboxCheckedChange'
      });
      /**
       * Defines whether the checkbox is indeterminate.
       */
      this.indeterminate = input(false, {
        alias: 'ngpCheckboxIndeterminate',
        transform: booleanAttribute
      });
      /**
       * The event that is emitted when the indeterminate value changes.
       */
      this.indeterminateChange = output({
        alias: 'ngpCheckboxIndeterminateChange'
      });
      /**
       * Whether the checkbox is required.
       */
      this.required = input(false, {
        alias: 'ngpCheckboxRequired',
        transform: booleanAttribute
      });
      /**
       * Defines whether the checkbox is disabled.
       */
      this.disabled = input(false, {
        alias: 'ngpCheckboxDisabled',
        transform: booleanAttribute
      });
      /**
       * The state of the checkbox.
       */
      this.state = checkboxState(this);
      setupFormControl({
        id: this.state.id,
        disabled: this.state.disabled
      });
      setupInteractions({
        hover: true,
        press: true,
        focusVisible: true,
        disabled: this.state.disabled
      });
    }
    onEnter(event) {
      // According to WAI ARIA, Checkboxes don't activate on enter keypress
      event.preventDefault();
    }
    toggle(event) {
      if (this.state.disabled()) {
        return;
      }
      // prevent this firing twice in cases where the label is clicked and the checkbox is clicked by the one event
      event?.preventDefault();
      const checked = this.state.indeterminate() ? true : !this.state.checked();
      this.state.checked.set(checked);
      this.checkedChange.emit(checked);
      // if the checkbox was indeterminate, it isn't anymore
      if (this.state.indeterminate()) {
        this.state.indeterminate.set(false);
        this.indeterminateChange.emit(false);
      }
    }
    static {
      this.ɵfac = function NgpCheckbox_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgpCheckbox)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgpCheckbox,
        selectors: [["", "ngpCheckbox", ""]],
        hostAttrs: ["role", "checkbox"],
        hostVars: 5,
        hostBindings: function NgpCheckbox_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("keydown.enter", function NgpCheckbox_keydown_enter_HostBindingHandler($event) {
              return ctx.onEnter($event);
            })("click", function NgpCheckbox_click_HostBindingHandler($event) {
              return ctx.toggle($event);
            })("keydown.space", function NgpCheckbox_keydown_space_HostBindingHandler($event) {
              return ctx.toggle($event);
            });
          }
          if (rf & 2) {
            i0.ɵɵhostProperty("tabindex", ctx.state.disabled() ? -1 : 0);
            i0.ɵɵattribute("aria-checked", ctx.state.indeterminate() ? "mixed" : ctx.state.checked())("data-checked", ctx.state.checked() ? "" : null)("data-indeterminate", ctx.state.indeterminate() ? "" : null)("aria-disabled", ctx.state.disabled());
          }
        },
        inputs: {
          id: [1, "id"],
          checked: [1, "ngpCheckboxChecked", "checked"],
          indeterminate: [1, "ngpCheckboxIndeterminate", "indeterminate"],
          required: [1, "ngpCheckboxRequired", "required"],
          disabled: [1, "ngpCheckboxDisabled", "disabled"]
        },
        outputs: {
          checkedChange: "ngpCheckboxCheckedChange",
          indeterminateChange: "ngpCheckboxIndeterminateChange"
        },
        features: [i0.ɵɵProvidersFeature([provideCheckboxState()])]
      });
    }
  }
  return NgpCheckbox;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgpCheckbox, injectCheckboxState, provideCheckboxState };
